import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumber(
  value: number,
  decimals: number = 2,
  isPercentage: boolean = false,
): string {
  if (typeof value !== "number" || !isFinite(value)) {
    return "N/A";
  }

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const formattedValue = formatter.format(value);
  return isPercentage ? `${formattedValue}%` : formattedValue;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const result = document.execCommand("copy");
      document.body.removeChild(textArea);
      return result;
    }
  } catch {
    return false;
  }
}

// Generate copyable text for performance drops data
export function generatePerformanceDropsReport(
  campaigns: any[],
  summary: any,
): string {
  const header = `PERFORMANCE DROPS ANALYSIS REPORT
Generated: ${new Date().toLocaleString()}
===============================================

SUMMARY:
- Total campaigns with performance drops: ${campaigns.length}
- Combined weekly spend affected: $${formatNumber(summary?.totalSpend || 0, 0)}
- Worst performing campaign: ${summary?.worstCampaign?.name || "N/A"}

CAMPAIGN DETAILS:
`;

  const campaignDetails = campaigns
    .map((campaign, index) => {
      return `${index + 1}. Campaign: ${campaign.Campaign}
   - Impressions Drop: ${formatNumber(campaign.ImprDrop13W || 0, 1)}%
   - Conversions Drop: ${formatNumber(campaign.ConvDrop13W || 0, 1)}%
   - Current Weekly Impressions: ${formatNumber(campaign.ImprAvg13W || 0, 0)}
   - Current Weekly Conversions: ${formatNumber(campaign.ConvAvg13W || 0, 1)}
   - Weekly Spend: $${formatNumber(campaign.CostAvg13W || 0, 0)}
   - Campaign ID: ${campaign.CampaignId || "N/A"}`;
    })
    .join("\n\n");

  const footer = `\nRECOMMENDED ACTIONS:
1. Review these campaigns for recent changes (keywords, ads, targeting)
2. Check for seasonal trends or external factors
3. Consider pausing underperforming elements
4. Analyze competitor activity in these markets
5. Review budget allocation to better performing campaigns

End of Report`;

  return header + campaignDetails + footer;
}

// Generate copyable text for campaign settings issues
export function generateCampaignSettingsReport(
  type: string,
  data: any,
): string {
  const timestamp = new Date().toLocaleString();

  switch (type) {
    case "geo-targeting":
      return `GEO TARGETING ISSUES REPORT
Generated: ${timestamp}
===============================================

SUMMARY:
- Campaigns with geo targeting issues: ${data.issues?.length || 0}
- Estimated weekly waste: $${formatNumber(data.summary?.estimatedWaste || 0, 0)}

AFFECTED CAMPAIGNS:
${
  data.issues
    ?.map(
      (issue: any, index: number) =>
        `${index + 1}. ${issue.campaign}
   - Current Setting: ${issue.geoTargetTypePos}
   - Weekly Spend: $${formatNumber(issue.cost || 0, 0)}
   - Campaign ID: ${issue.campaignId || "N/A"}`,
    )
    .join("\n\n") || "No issues found"
}

RECOMMENDED ACTION:
Change geo targeting from "Presence or Interest" to "Presence" to reduce wasted spend on users outside your target location.

End of Report`;

    case "search-partners":
      return `SEARCH PARTNERS ANALYSIS REPORT
Generated: ${timestamp}
===============================================

PERFORMANCE COMPARISON:
- Google Search Only Campaigns: ${data.searchOnly?.campaigns || 0}
- Average CPA (Search Only): $${formatNumber(data.searchOnly?.avgCpa || 0, 2)}
- Search Partners Enabled Campaigns: ${data.searchPartners?.campaigns || 0}  
- Average CPA (Search Partners): $${formatNumber(data.searchPartners?.avgCpa || 0, 2)}

RECOMMENDATION: ${data.recommendation === "disable" ? "DISABLE Search Partners" : "KEEP Search Partners enabled"}

RATIONALE:
${
  data.recommendation === "disable"
    ? "Search Partners CPA is significantly higher than Google Search, indicating poor performance."
    : "Search Partners performance is acceptable compared to Google Search."
}

End of Report`;

    default:
      return `CAMPAIGN SETTINGS REPORT
Generated: ${timestamp}
Data: ${JSON.stringify(data, null, 2)}`;
  }
}

// Generate copyable text for account health issues
export function generateAccountHealthReport(type: string, data: any): string {
  const timestamp = new Date().toLocaleString();

  switch (type) {
    case "disapproved-ads":
      return `DISAPPROVED ADS REPORT
Generated: ${timestamp}
===============================================

SUMMARY:
- Total disapproved ads: ${data.totalDisapproved || 0}
- Affected campaigns: ${data.affectedCampaigns || 0}
- Worst campaign: ${data.worstCampaign?.name || "N/A"} (${data.worstCampaign?.disapprovedCount || 0} ads)

AFFECTED AD GROUPS:
${
  data.worstCampaign?.adGroups
    ?.map((group: string, index: number) => `${index + 1}. ${group}`)
    .join("\n") || "No disapproved ads found"
}

RECOMMENDED ACTIONS:
1. Review disapproved ads in Google Ads interface
2. Identify policy violations (trademark, misleading claims, etc.)
3. Edit ads to comply with Google Ads policies
4. Resubmit for review after corrections
5. Create compliant replacement ads

End of Report`;

    case "empty-ad-groups":
      return `EMPTY AD GROUPS REPORT
Generated: ${timestamp}
===============================================

SUMMARY:
- Total empty ad groups: ${data.totalEmptyGroups || 0}
- Affected campaigns: ${data.affectedCampaigns || 0}
- Worst campaign: ${data.worstCampaign?.name || "N/A"} (${data.worstCampaign?.emptyGroupCount || 0} groups)

EMPTY AD GROUPS:
${
  data.worstCampaign?.adGroups
    ?.map((group: string, index: number) => `${index + 1}. ${group}`)
    .join("\n") || "No empty ad groups found"
}

RECOMMENDED ACTIONS:
1. Add responsive search ads to empty ad groups
2. Consider deleting unnecessary empty ad groups
3. Ensure each ad group has 2-3 ads for optimal performance
4. Check if keywords need to be moved to active ad groups

End of Report`;

    default:
      return `ACCOUNT HEALTH REPORT
Generated: ${timestamp}
Data: ${JSON.stringify(data, null, 2)}`;
  }
}
